if (!customElements.get('cocktail-builder')) {
  customElements.define('cocktail-builder', class extends HTMLElement {
    connectedCallback() {
      this.tiers = [
        { qty: parseInt(this.dataset.tier1Qty) || 1, pct: parseInt(this.dataset.tier1Pct) || 10 },
        { qty: parseInt(this.dataset.tier2Qty) || 2, pct: parseInt(this.dataset.tier2Pct) || 20 },
        { qty: parseInt(this.dataset.tier3Qty) || 3, pct: parseInt(this.dataset.tier3Pct) || 30 },
      ];
      this.maxTierQty = this.tiers[this.tiers.length - 1].qty;
      this.currentStep = 1;
      // Furthest step the user has reached; lets them freely navigate back
      // and forward across already-completed steps via the step tabs.
      this._maxStepReached = 1;
      this.capsuleQty = {};
      this.capsuleItems = [];
      this.accessorySet = new Set();
      this.selectedMachine = null;
      this._drawerOpen = false;
      this._pendingRemove = null;
      const step1El = this.querySelector('.cb-step[data-step="1"] .cb-step__label');
      this._step1LabelText = step1El ? step1El.textContent.trim() : 'Capsules';
      const step2El = this.querySelector('.cb-step[data-step="2"] .cb-step__label');
      this._step2LabelText = step2El ? step2El.textContent.trim() : 'Cocktail Maker';
      const step3El = this.querySelector('.cb-step[data-step="3"] .cb-step__label');
      this._step3LabelText = step3El ? step3El.textContent.trim() : 'Accessories';

      this._cacheRefs();
      this._buildPriceMap();
      this._restoreState();
      this._bindEvents();
      this._applyFilter();
      this._render();
      this._trackHeaderHeight();
    }

    /* #15 — cleanup ResizeObserver on disconnect */
    disconnectedCallback() {
      if (this._headerRO) {
        this._headerRO.disconnect();
        this._headerRO = null;
      }
    }

    _ref(name) {
      return this.querySelector('[data-ref="' + name + '"]');
    }

    _cacheRefs() {
      this.refs = {
        announcement: this._ref('announcement'),
        announcementText: this._ref('announcementText'),
        announcementIcon: this._ref('announcementIcon'),
        capsuleCart: this._ref('capsuleCart'),
        progressFill: this._ref('progressFill'),
        barWrapper: this._ref('barWrapper'),
        filterSelect: this._ref('filterSelect'),
        filterBadges: this._ref('filterBadges'),
        productsGrid: this._ref('productsGrid'),
        accessoriesGrid: this._ref('accessoriesGrid'),
        machineGrid: this._ref('machineGrid'),
        machineTabs: this._ref('machineTabs'),
        bottomSavings: this._ref('bottomSavings'),
        bottomCta: this._ref('bottomCta'),
        ctaLine1: this._ref('ctaLine1'),
        ctaLine2: this._ref('ctaLine2'),
        step1Status: this._ref('step1Status'),
        step2Status: this._ref('step2Status'),
        step3Status: this._ref('step3Status'),
        // Sidebar refs
        sidebarCart: this._ref('sidebarCart'),
        sidebarProgressFill: this._ref('sidebarProgressFill'),
        sidebarBarWrapper: this._ref('sidebarBarWrapper'),
        sidebarSavings: this._ref('sidebarSavings'),
        sidebarCta: this._ref('sidebarCta'),
        sidebarCtaLine1: this._ref('sidebarCtaLine1'),
        sidebarCtaLine2: this._ref('sidebarCtaLine2'),
        sidebarStep1Status: this._ref('sidebarStep1Status'),
        sidebarStep2Status: this._ref('sidebarStep2Status'),
        sidebarStep3Status: this._ref('sidebarStep3Status'),
        // Drawer refs
        drawer: this._ref('drawer'),
        drawerOverlay: this._ref('drawerOverlay'),
        drawerClose: this._ref('drawerClose'),
        drawerBody: this._ref('drawerBody'),
        drawerTotalOriginal: this._ref('drawerTotalOriginal'),
        drawerTotalSale: this._ref('drawerTotalSale'),
        drawerCheckout: this._ref('drawerCheckout'),
        // Warning refs
        warningDialog: this._ref('warningDialog'),
        warningText: this._ref('warningText'),
        warningClose: this._ref('warningClose'),
        warningConfirm: this._ref('warningConfirm'),
        warningCancel: this._ref('warningCancel'),
        // Review buttons
        reviewBtn: this._ref('reviewBtn'),
        sidebarReviewBtn: this._ref('sidebarReviewBtn'),
        // Skip links (step 3 — accessories are optional)
        bottomSkip: this._ref('bottomSkip'),
        sidebarSkip: this._ref('sidebarSkip'),
      };
      this._steps = this.querySelectorAll('.cb-step');
      this._panels = this.querySelectorAll('.cb-panel');
      this._balls = this.querySelectorAll('.cb-progress__ball');

      /* #7 — cache SVG templates */
      this._tplPlaceholder = this.querySelector('#cb-tpl-placeholder');
      this._tplRemove = this.querySelector('#cb-tpl-remove');
      this._tplChange = this.querySelector('#cb-tpl-change');
    }

    /* #8 — build price map to avoid repeated DOM queries */
    _buildPriceMap() {
      this._priceMap = {};
      this.refs.productsGrid.querySelectorAll('.cb-product-card').forEach(card => {
        this._priceMap[card.dataset.productId] = parseInt(card.dataset.price) || 0;
      });
      if (this.refs.machineGrid) {
        this.refs.machineGrid.querySelectorAll('.cb-machine-card').forEach(card => {
          this._priceMap[card.dataset.productId] = parseInt(card.dataset.price) || 0;
        });
      }
      if (this.refs.accessoriesGrid) {
        this.refs.accessoriesGrid.querySelectorAll('.cb-product-card').forEach(card => {
          this._priceMap[card.dataset.productId] = parseInt(card.dataset.price) || 0;
        });
      }
    }

    // ── Getters ──
    get totalCapsules() { return this.capsuleItems.length; }

    get currentTierIndex() {
      for (let i = this.tiers.length - 1; i >= 0; i--) {
        if (this.totalCapsules >= this.tiers[i].qty) return i;
      }
      return null;
    }

    get nextTierIndex() {
      for (let i = 0; i < this.tiers.length; i++) {
        if (this.totalCapsules < this.tiers[i].qty) return i;
      }
      return null;
    }

    // ── Events ──
    _bindEvents() {
      this.refs.productsGrid.querySelectorAll('.cb-product-card').forEach(card => {
        if (card.dataset.unavailable === 'true') return;
        const pid = card.dataset.productId;
        card.querySelector('.cb-add-btn').addEventListener('click', () => this._addItem(pid));
        card.querySelector('[data-action="plus"]').addEventListener('click', () => this._addItem(pid));
        card.querySelector('[data-action="minus"]').addEventListener('click', () => this._removeItemByProduct(pid));
      });

      this.refs.filterSelect.addEventListener('change', () => {
        this._activeFilter = this.refs.filterSelect.value;
        this._syncFilterBadges();
        this._applyFilter();
      });

      this.refs.filterBadges.querySelectorAll('.cb-filter__badge').forEach(badge => {
        badge.addEventListener('click', () => {
          this._activeFilter = badge.dataset.filter;
          this._syncFilterSelect();
          this._syncFilterBadges();
          this._applyFilter();
        });
      });

      this._activeFilter = 'popular';

      this._steps.forEach(step => {
        step.addEventListener('click', () => {
          const n = parseInt(step.dataset.step);
          if (n <= this._maxStepReached) this._goToStep(n);
        });
      });

      // Accessory card events
      if (this.refs.accessoriesGrid) {
        this.refs.accessoriesGrid.querySelectorAll('.cb-accessory-card').forEach(card => {
          if (card.dataset.unavailable === 'true') return;
          const pid = card.dataset.productId;
          card.querySelector('.cb-accessory-add').addEventListener('click', () => this._toggleAccessory(pid, true));
          card.querySelector('.cb-accessory-remove').addEventListener('click', () => this._toggleAccessory(pid, false));
        });
      }

      // Machine card events
      if (this.refs.machineGrid) {
        this.refs.machineGrid.querySelectorAll('.cb-machine-card').forEach(card => {
          card.querySelector('[data-action="select-machine"]').addEventListener('click', () => {
            this._selectMachine(card.dataset.productId);
          });
        });
      }

      // Machine mobile tabs
      if (this.refs.machineTabs) {
        this.refs.machineTabs.querySelectorAll('.cb-machine-tab').forEach(tab => {
          tab.addEventListener('click', () => {
            const idx = parseInt(tab.dataset.tabIndex);
            this.refs.machineTabs.querySelectorAll('.cb-machine-tab').forEach(t => t.classList.remove('active'));
            tab.classList.add('active');
            const cards = this.refs.machineGrid.querySelectorAll('.cb-machine-card');
            cards.forEach((c, i) => c.classList.toggle('active', i === idx));
          });
        });
      }

      const ctaHandler = () => {
        if (this.currentStep === 1 && this.totalCapsules > 0) this._goToStep(2);
        else if (this.currentStep === 2 && this.selectedMachine) this._goToStep(3);
        // Step 3 (accessories) is optional — always allowed to review the bundle.
        else if (this.currentStep === 3) this._openDrawer();
      };
      this.refs.bottomCta.addEventListener('click', ctaHandler);
      this.refs.sidebarCta.addEventListener('click', ctaHandler);

      // Review Bundle buttons
      const openDrawer = () => this._openDrawer();
      this.refs.reviewBtn.addEventListener('click', openDrawer);
      this.refs.sidebarReviewBtn.addEventListener('click', openDrawer);

      // Skip accessories — same destination as the CTA, just without a selection.
      [this.refs.bottomSkip, this.refs.sidebarSkip].forEach(el => {
        if (el) el.addEventListener('click', openDrawer);
      });

      // Drawer close
      this.refs.drawerClose.addEventListener('click', () => this._closeDrawer());
      this.refs.drawerOverlay.addEventListener('click', () => this._closeDrawer());

      // Warning dialog
      this.refs.warningClose.addEventListener('click', () => this._closeWarning());
      this.refs.warningCancel.addEventListener('click', () => this._closeWarning());
      this.refs.warningConfirm.addEventListener('click', () => this._confirmRemove());
      this.refs.warningDialog.addEventListener('close', () => { this._pendingRemove = null; });

      // Checkout
      this.refs.drawerCheckout.addEventListener('click', () => this._checkout());
    }

    // ── State mutations ──
    _addItem(productId) {
      const card = this.refs.productsGrid.querySelector('[data-product-id="' + productId + '"]');
      if (!card) return;
      this.capsuleQty[productId] = (this.capsuleQty[productId] || 0) + 1;
      this.capsuleItems.push({
        productId,
        variantId: card.dataset.variantId,
        image: card.dataset.productImage,
        title: card.dataset.productTitle,
      });
      this._updateCardQty(productId);
      this._render();
    }

    _removeItem(index) {
      const item = this.capsuleItems[index];
      if (!item) return;
      this.capsuleItems.splice(index, 1);
      this.capsuleQty[item.productId] = Math.max(0, (this.capsuleQty[item.productId] || 1) - 1);
      this._updateCardQty(item.productId);
      this._render();
    }

    _removeItemByProduct(productId) {
      for (let i = this.capsuleItems.length - 1; i >= 0; i--) {
        if (this.capsuleItems[i].productId === productId) {
          this._removeItem(i);
          return;
        }
      }
    }

    _toggleAccessory(productId, adding) {
      if (!this.refs.accessoriesGrid) return;
      const card = this.refs.accessoriesGrid.querySelector('[data-product-id="' + productId + '"]');
      if (!card) return;
      if (adding) {
        this.accessorySet.add(productId);
      } else {
        this.accessorySet.delete(productId);
      }
      const isAdded = this.accessorySet.has(productId);
      card.classList.toggle('has-items', isAdded);
      card.querySelector('.cb-accessory-add').style.display = isAdded ? 'none' : 'flex';
      card.querySelector('.cb-accessory-remove').style.display = isAdded ? 'flex' : 'none';
      this._render();
    }

    _selectMachine(productId) {
      if (!this.refs.machineGrid) return;
      this.selectedMachine = productId;
      this._renderMachinePrices();
      this._render();
    }

    _formatPrice(cents) {
      return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }

    _discountedPriceCents(card) {
      const price = parseInt(card.dataset.price) || 0;
      const idx = this.currentTierIndex;
      const pct = idx !== null ? this.tiers[idx].pct : 0;
      return price - Math.floor(price * pct / 100);
    }

    _renderMachinePrices() {
      if (!this.refs.machineGrid) return;
      const idx = this.currentTierIndex;
      const cards = this.refs.machineGrid.querySelectorAll('.cb-machine-card');
      cards.forEach(card => {
        const origEl = card.querySelector('.cb-machine-card__price-original');
        const saleEl = card.querySelector('.cb-machine-card__price-sale');
        const price = parseInt(card.dataset.price) || 0;
        const discounted = this._discountedPriceCents(card);
        if (origEl && saleEl) {
          if (idx !== null) {
            origEl.style.display = '';
            saleEl.textContent = this._formatPrice(discounted);
          } else {
            origEl.style.display = 'none';
            saleEl.textContent = this._formatPrice(price);
          }
        }
        const isSel = card.dataset.productId === this.selectedMachine;
        const labelEl = card.querySelector('.cb-machine-card__cta-label');
        const priceEl = card.querySelector('.cb-machine-card__cta-price');
        const btnEl = card.querySelector('[data-action="select-machine"]');
        if (isSel) {
          labelEl.textContent = 'Added';
          priceEl.textContent = '';
          if (btnEl) btnEl.disabled = true;
        } else {
          labelEl.textContent = 'Add -';
          priceEl.textContent = this._formatPrice(discounted);
          if (btnEl) btnEl.disabled = false;
        }
      });
    }

    _updateCardQty(productId) {
      const card = this.refs.productsGrid.querySelector('[data-product-id="' + productId + '"]');
      if (!card) return;
      const qty = this.capsuleQty[productId] || 0;
      const addBtn = card.querySelector('.cb-add-btn');
      const qtySelector = card.querySelector('.cb-qty-selector');
      card.classList.toggle('has-items', qty > 0);
      if (qty > 0) {
        addBtn.style.display = 'none';
        qtySelector.style.display = 'flex';
        qtySelector.querySelector('.cb-qty-selector__value').textContent = qty;
      } else {
        addBtn.style.display = '';
        qtySelector.style.display = 'none';
      }
    }

    // ── Render ──
    _render() {
      this._renderAnnouncement();
      this._renderCart();
      this._renderProgress();
      this._renderBottomBanner();
      this._renderMachinePrices();
      this._saveState();
    }

    _renderAnnouncement() {
      const idx = this.currentTierIndex;
      if (idx === null) {
        this.refs.announcement.classList.remove('visible');
        return;
      }
      this.refs.announcement.classList.add('visible');
      const pct = this.tiers[idx].pct;
      const next = this.nextTierIndex;
      let html;
      if (next === null) {
        html = '<strong>Max Savings Unlocked!</strong> <br>You\'ve <strong>Unlocked ' + pct + '%</strong> off!';
        this.refs.announcementIcon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M12.5 1.08789C13.3671 1.08795 14.2209 1.30724 14.9814 1.72363C15.5444 2.03197 16.0435 2.44235 16.4541 2.93066C17.0942 2.87216 17.7422 2.93325 18.3633 3.11328C19.2 3.3559 19.9621 3.80684 20.5781 4.42285C21.194 5.03886 21.6442 5.80108 21.8867 6.6377C22.0666 7.2585 22.1278 7.90615 22.0693 8.5459C22.558 8.95674 22.9689 9.45615 23.2773 10.0195C23.6936 10.78 23.9121 11.634 23.9121 12.501C23.912 13.3678 23.6936 14.2211 23.2773 14.9814C22.9687 15.5451 22.5574 16.0441 22.0684 16.4551C22.1263 17.0936 22.0661 17.7397 21.8867 18.3594C21.6447 19.1948 21.1954 19.9566 20.5811 20.5723C19.9668 21.1877 19.2068 21.6381 18.3721 21.8818C17.7525 22.0627 17.1058 22.1251 16.4668 22.0684C16.0559 22.5594 15.5568 22.9722 14.9922 23.2822C14.2304 23.7005 13.3749 23.9198 12.5059 23.9199C11.6367 23.9199 10.7805 23.7004 10.0186 23.2822C9.45427 22.9723 8.95468 22.56 8.54395 22.0693C7.9059 22.1271 7.25976 22.066 6.64062 21.8867C5.80553 21.6448 5.04424 21.1961 4.42871 20.582C3.81312 19.9677 3.36293 19.2069 3.11914 18.3721C2.93846 17.7531 2.87518 17.1072 2.93164 16.4688C2.43879 16.058 2.02429 15.5576 1.71289 14.9922C1.29285 14.2292 1.07242 13.3719 1.07227 12.501C1.07227 11.6297 1.29275 10.772 1.71289 10.0088C2.02443 9.44294 2.43841 8.94223 2.93164 8.53125C2.87529 7.89315 2.9386 7.2476 3.11914 6.62891C3.36286 5.79409 3.81318 5.03428 4.42871 4.41992C5.04435 3.80557 5.80525 3.35629 6.64062 3.11426C7.26015 2.93486 7.90647 2.87375 8.54492 2.93164C8.95583 2.44266 9.45495 2.03228 10.0186 1.72363C10.7792 1.30718 11.6328 1.08794 12.5 1.08789ZM16.332 9.70996C15.9415 9.31944 15.3085 9.31944 14.918 9.70996L11.458 13.1689L10.082 11.793C9.69155 11.4027 9.05845 11.4027 8.66797 11.793C8.27764 12.1835 8.27765 12.8165 8.66797 13.207L10.751 15.291C11.1414 15.6812 11.7745 15.681 12.165 15.291L16.332 11.124C16.7225 10.7336 16.7224 10.1005 16.332 9.70996Z" fill="white"/></svg>';
      } else {
        html = 'You\'ve <strong>Unlocked ' + pct + '%</strong> off!';
        const needed = this.tiers[next].qty - this.totalCapsules;
        html += '<br><span class="cb-announcement__next-tier">Add ' + needed + ' more for additional ' + this.tiers[next].pct + '% off!</span>';
        this.refs.announcementIcon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21.0353 12.0004C21.0353 11.4901 20.9063 10.9874 20.6612 10.5398C20.4162 10.0924 20.0626 9.714 19.6328 9.43917C19.2924 9.22151 19.1251 8.81338 19.2137 8.41917C19.3261 7.91907 19.3096 7.39837 19.1669 6.90605C19.0241 6.41377 18.7596 5.96537 18.3972 5.60292C18.0348 5.2405 17.5863 4.976 17.0941 4.83324C16.6018 4.69054 16.081 4.674 15.5809 4.78636C15.1867 4.87499 14.7786 4.70771 14.5609 4.3673C14.286 3.9374 13.9079 3.583 13.4603 3.33792C13.0127 3.09284 12.51 2.9648 11.9997 2.9648C11.4895 2.96485 10.9875 3.09296 10.54 3.33792C10.0924 3.583 9.71333 3.93737 9.43843 4.3673C9.22088 4.70729 8.81419 4.87457 8.4203 4.78636C7.92086 4.67439 7.40069 4.69085 6.90905 4.83324C6.4176 4.97561 5.97002 5.23967 5.6078 5.60105C5.2455 5.9626 4.97966 6.41002 4.83624 6.90136C4.69288 7.3926 4.67662 7.91211 4.78749 8.41167C4.8752 8.80677 4.70571 9.21522 4.36374 9.43167C3.93051 9.70585 3.57321 10.085 3.32593 10.5342C3.07876 10.9832 2.94905 11.4878 2.94905 12.0004C2.94912 12.513 3.07872 13.0177 3.32593 13.4667C3.5732 13.9157 3.93061 14.2951 4.36374 14.5692C4.70546 14.7857 4.87517 15.1933 4.78749 15.5882C4.6766 16.0878 4.6929 16.6073 4.83624 17.0985C4.97966 17.5899 5.2455 18.0373 5.6078 18.3989C5.97002 18.7603 6.41758 19.0243 6.90905 19.1667C7.40069 19.3091 7.92086 19.3255 8.4203 19.2135L8.56843 19.192C8.91393 19.1697 9.25028 19.3358 9.4403 19.6345C9.71482 20.066 10.0936 20.4214 10.5419 20.6676C10.9902 20.9138 11.4938 21.0426 12.0053 21.0426C12.5166 21.0426 13.0196 20.9137 13.4678 20.6676C13.9162 20.4214 14.2958 20.0661 14.5703 19.6345L14.6594 19.5154C14.8879 19.2558 15.2432 19.136 15.5884 19.2126C16.088 19.3235 16.6075 19.3063 17.0987 19.1629C17.59 19.0195 18.0375 18.7546 18.3991 18.3923C18.7604 18.0301 19.0245 17.5825 19.1669 17.091C19.3093 16.5994 19.3248 16.0792 19.2128 15.5798C19.1246 15.1859 19.2928 14.7792 19.6328 14.5617C20.0627 14.2868 20.4162 13.9076 20.6612 13.4601C20.9063 13.0126 21.0352 12.5106 21.0353 12.0004ZM22.9553 12.0004C22.9552 12.8327 22.7456 13.6516 22.3459 14.3817C22.0496 14.9229 21.6548 15.4018 21.1853 15.7964C21.241 16.4095 21.1832 17.0295 21.0109 17.6245C20.7786 18.4266 20.3474 19.1577 19.7575 19.7489C19.1677 20.3398 18.4383 20.7721 17.6369 21.0061C17.0419 21.1797 16.4214 21.2396 15.8078 21.1851C15.4133 21.6566 14.9343 22.0527 14.3922 22.3504C13.6607 22.752 12.8397 22.9626 12.0053 22.9626C11.1708 22.9626 10.349 22.7521 9.61749 22.3504C9.07561 22.0528 8.59625 21.6573 8.20187 21.186C7.58919 21.2415 6.96919 21.1829 6.37468 21.0107C5.5728 20.7784 4.84222 20.3479 4.25124 19.7582C3.66014 19.1684 3.22806 18.4383 2.99405 17.6367C2.82056 17.0423 2.75978 16.4225 2.81405 15.8095C2.34077 15.415 1.94303 14.935 1.64405 14.392C1.24074 13.6594 1.02912 12.8367 1.02905 12.0004C1.02905 11.164 1.24064 10.3407 1.64405 9.60792C1.94314 9.0647 2.34054 8.58403 2.81405 8.18949C2.75994 7.57679 2.82066 6.95729 2.99405 6.36324C3.22803 5.56179 3.6603 4.8324 4.25124 4.24261C4.84225 3.65284 5.57272 3.22152 6.37468 2.98917C6.96948 2.81691 7.58982 2.75828 8.2028 2.81386C8.59728 2.34444 9.07643 1.95047 9.61749 1.65417C10.3477 1.25436 11.1672 1.04485 11.9997 1.0448C12.8322 1.0448 13.6516 1.25438 14.3819 1.65417C14.9225 1.95021 15.4013 2.34401 15.7956 2.81292C16.4102 2.75671 17.0321 2.81538 17.6284 2.98824C18.4318 3.22112 19.1632 3.65394 19.7547 4.24542C20.3461 4.83687 20.778 5.56834 21.0109 6.37167C21.1837 6.96781 21.2423 7.58921 21.1862 8.20355C21.6556 8.59802 22.0497 9.07722 22.3459 9.61824C22.7457 10.3484 22.9553 11.1679 22.9553 12.0004Z" fill="white"/><path d="M14.3213 9.32122C14.6962 8.94631 15.3039 8.94631 15.6788 9.32122C16.0537 9.69612 16.0537 10.3038 15.6788 10.6787L11.6784 14.679C11.3035 15.0536 10.6957 15.0538 10.3209 14.679L8.32126 12.6784C7.94651 12.3035 7.94641 11.6958 8.32126 11.3209C8.69612 10.9462 9.3039 10.9462 9.67876 11.3209L10.9997 12.6418L14.3213 9.32122Z" fill="white"/></svg>';
      }
      this.refs.announcementText.innerHTML = html;
    }

    _createCartSlot(image, title, btnTemplate, ariaLabel, onClick) {
      const el = document.createElement('div');
      el.className = 'cb-capsule-cart__item cb-capsule-cart__item--filled';
      el.innerHTML = '<img src="' + image + '" alt="' + (title || '') + '" width="80" height="80">';
      const btn = document.createElement('button');
      btn.className = 'cb-capsule-cart__remove';
      btn.setAttribute('aria-label', ariaLabel);
      if (btnTemplate) btn.appendChild(btnTemplate.content.cloneNode(true));
      btn.addEventListener('click', onClick);
      el.appendChild(btn);
      return el;
    }

    _createPlaceholderSlot() {
      const ph = document.createElement('div');
      ph.className = 'cb-capsule-cart__item cb-capsule-cart__item--placeholder';
      if (this._tplPlaceholder) ph.appendChild(this._tplPlaceholder.content.cloneNode(true));
      return ph;
    }

    _populateCapsuleCart(container, minSlots) {
      container.innerHTML = '';

      this.capsuleItems.forEach((item, i) => {
        container.appendChild(
          this._createCartSlot(item.image, item.title, this._tplRemove, 'Remove ' + item.title, () => this._removeItem(i))
        );
      });

      const totalSlots = Math.max(minSlots, this.capsuleItems.length + 1);
      for (let p = this.capsuleItems.length; p < totalSlots; p++) {
        container.appendChild(this._createPlaceholderSlot());
      }
    }

    _populateSidebarCart(container, minSlots) {
      container.innerHTML = '';
      let filledCount = 0;

      // Machine — always first
      if (this.selectedMachine && this.refs.machineGrid) {
        const card = this.refs.machineGrid.querySelector('[data-product-id="' + this.selectedMachine + '"]');
        if (card) {
          container.appendChild(
            this._createCartSlot(card.dataset.productImage, card.dataset.productTitle, this._tplChange, 'Change', () => this._goToStep(2))
          );
          filledCount++;
        }
      }

      // Capsules
      this.capsuleItems.forEach((item, i) => {
        container.appendChild(
          this._createCartSlot(item.image, item.title, this._tplRemove, 'Remove ' + item.title, () => this._removeItem(i))
        );
        filledCount++;
      });

      // Accessories
      this.accessorySet.forEach(pid => {
        const card = this.refs.accessoriesGrid ? this.refs.accessoriesGrid.querySelector('[data-product-id="' + pid + '"]') : null;
        if (card) {
          container.appendChild(
            this._createCartSlot(card.dataset.productImage, card.dataset.productTitle, this._tplRemove, 'Remove ' + card.dataset.productTitle, () => this._toggleAccessory(pid, false))
          );
          filledCount++;
        }
      });

      // Placeholders
      const totalSlots = Math.max(minSlots, filledCount + 1);
      for (let p = filledCount; p < totalSlots; p++) {
        container.appendChild(this._createPlaceholderSlot());
      }
    }

    _renderCart() {
      this._populateCapsuleCart(this.refs.capsuleCart, 3);
      this._populateSidebarCart(this.refs.sidebarCart, 6);
    }

    _renderProgress() {
      const pct = Math.min((this.totalCapsules / this.maxTierQty) * 100, 100);
      this.refs.progressFill.style.width = pct + '%';
      this.refs.sidebarProgressFill.style.width = pct + '%';

      this._balls.forEach(ball => {
        const tierIdx = parseInt(ball.dataset.tierIndex);
        ball.classList.toggle('reached', this.totalCapsules >= this.tiers[tierIdx].qty);
      });

      // Keep step completed/active state in sync with current selections.
      this._updateStepClasses();
    }

    _renderBottomBanner() {
      const idx = this.currentTierIndex;
      const next = this.nextTierIndex;
      const total = this.totalCapsules;

      // Savings badge (both mobile + sidebar)
      const savingsHtml = idx !== null ? this.tiers[idx].pct + '% <strong>Savings Unlocked</strong>' : '';
      [this.refs.bottomSavings, this.refs.sidebarSavings].forEach(el => {
        if (idx !== null) {
          el.innerHTML = savingsHtml;
          el.classList.add('visible');
        } else {
          el.classList.remove('visible');
        }
      });

      // Step statuses
      this.refs.step1Status.textContent = total + ' selected';
      this.refs.sidebarStep1Status.textContent = total + ' selected';
      let machineText = '.....';
      if (this.selectedMachine && this.refs.machineGrid) {
        const selCard = this.refs.machineGrid.querySelector('[data-product-id="' + this.selectedMachine + '"]');
        const titleEl = selCard ? selCard.querySelector('.cb-machine-card__title') : null;
        machineText = titleEl ? titleEl.textContent.trim() : '1 selected';
      }
      this.refs.step2Status.textContent = machineText;
      this.refs.sidebarStep2Status.textContent = machineText;
      const accCount = this.accessorySet.size;
      const accText = accCount > 0 ? accCount + ' selected' : '.....';
      this.refs.step3Status.textContent = accText;
      this.refs.sidebarStep3Status.textContent = accText;

      // CTA — step-aware
      let line1, line2, hasItems, disabled;

      if (this.currentStep === 1) {
        hasItems = total > 0;
        disabled = false;
        line1 = total > 0 ? 'Add Cocktails & Continue' : 'Select Cocktails';
        if (idx === null) {
          const needed = this.tiers[0].qty - total;
          const s = needed === 1 ? '' : 's';
          line2 = 'Add ' + needed + ' cocktail' + s + ' to unlock ' + this.tiers[0].pct + '% savings';
        } else if (next !== null) {
          const needed = this.tiers[next].qty - total;
          const s = needed === 1 ? '' : 's';
          line2 = 'Add ' + needed + ' more cocktail' + s + ' to unlock ' + this.tiers[next].pct + '% savings';
        } else {
          line2 = 'Max Savings Unlocked';
        }
      } else if (this.currentStep === 2) {
        hasItems = !!this.selectedMachine;
        disabled = !this.selectedMachine;
        line1 = this.selectedMachine ? 'Add ' + this._step2LabelText + ' & Continue' : 'Select ' + this._step2LabelText;
        line2 = '';
      } else {
        // Accessories are optional — the CTA stays live with nothing selected so
        // the user can always reach the bundle review.
        hasItems = accCount > 0;
        disabled = false;
        line1 = accCount > 0 ? 'Add ' + this._step3LabelText + ' & Review Bundle' : 'Select ' + this._step3LabelText;
        line2 = '';
      }

      // "Skip to checkout" sits under the CTA only while step 3 is empty.
      const showSkip = this.currentStep === 3 && accCount === 0;
      [this.refs.bottomSkip, this.refs.sidebarSkip].forEach(el => {
        if (el) el.hidden = !showSkip;
      });

      [this.refs.bottomCta, this.refs.sidebarCta].forEach(el => {
        el.classList.toggle('has-items', hasItems);
        el.disabled = disabled;
      });
      this.refs.ctaLine1.textContent = line1;
      this.refs.sidebarCtaLine1.textContent = line1;
      this.refs.ctaLine2.textContent = line2;
      this.refs.sidebarCtaLine2.textContent = line2;
    }

    // ── Filter ──
    _applyFilter() {
      const val = this._activeFilter;
      const cards = this.refs.productsGrid.querySelectorAll('.cb-product-card');
      // Check if any cards have the selected filter tag
      let anyMatch = false;
      cards.forEach(card => {
        const filters = (card.dataset.filters || '').split(',').filter(Boolean);
        if (filters.includes(val)) anyMatch = true;
      });
      // If no cards match (tags not set up yet), show all
      cards.forEach(card => {
        const filters = (card.dataset.filters || '').split(',').filter(Boolean);
        const hidden = anyMatch ? !filters.includes(val) : false;
        card.setAttribute('data-hidden', hidden ? 'true' : 'false');
      });
    }

    _syncFilterBadges() {
      this.refs.filterBadges.querySelectorAll('.cb-filter__badge').forEach(b => {
        b.classList.toggle('active', b.dataset.filter === this._activeFilter);
      });
    }

    _syncFilterSelect() {
      this.refs.filterSelect.value = this._activeFilter;
    }

    // ── Session Storage ──
    _storageKey() { return 'cb_state_' + this.dataset.sectionId; }

    _saveState() {
      try {
        const state = {
          currentStep: this.currentStep,
          capsuleQty: this.capsuleQty,
          capsuleItems: this.capsuleItems,
          accessoryIds: [...this.accessorySet],
          selectedMachine: this.selectedMachine,
        };
        sessionStorage.setItem(this._storageKey(), JSON.stringify(state));
      } catch (e) { /* quota exceeded or private mode */ }
    }

    _restoreState() {
      try {
        const raw = sessionStorage.getItem(this._storageKey());
        if (!raw) return;
        const state = JSON.parse(raw);
        this.capsuleQty = state.capsuleQty || {};
        this.capsuleItems = state.capsuleItems || [];
        this.accessorySet = new Set(state.accessoryIds || []);
        this.selectedMachine = state.selectedMachine || null;
        // Restore card UI for capsules
        Object.keys(this.capsuleQty).forEach(pid => this._updateCardQty(pid));
        // Restore machine selection
        if (this.selectedMachine) this._selectMachine(this.selectedMachine);
        // Restore accessory cards
        this.accessorySet.forEach(pid => {
          const card = this.refs.accessoriesGrid ? this.refs.accessoriesGrid.querySelector('[data-product-id="' + pid + '"]') : null;
          if (card) {
            card.classList.add('has-items');
            card.querySelector('.cb-accessory-add').style.display = 'none';
            card.querySelector('.cb-accessory-remove').style.display = 'flex';
          }
        });
        // Restore step (must happen after caching refs)
        if (state.currentStep && state.currentStep > 1) {
          this._goToStep(state.currentStep);
        }
      } catch (e) { /* corrupted data, start fresh */ }
    }

    // ── Bundle Drawer ──
    _openDrawer() {
      this._renderDrawer();
      this.refs.drawer.classList.add('open');
      this.refs.drawerOverlay.classList.add('open');
      document.body.style.overflow = 'hidden';
      this._drawerOpen = true;
    }

    _closeDrawer() {
      this.refs.drawer.classList.remove('open');
      this.refs.drawerOverlay.classList.remove('open');
      document.body.style.overflow = '';
      this._drawerOpen = false;
    }

    _renderDrawer() {
      const body = this.refs.drawerBody;
      body.innerHTML = '';
      const idx = this.currentTierIndex;
      const pct = idx !== null ? this.tiers[idx].pct : 0;

      // Savings badge
      const savingsEl = document.createElement('span');
      savingsEl.className = 'cb-drawer__savings cb-savings-badge' + (idx !== null ? ' visible' : '');
      if (idx !== null) savingsEl.innerHTML = this.tiers[idx].pct + '% <strong>Savings Unlocked</strong>';
      body.appendChild(savingsEl);

      // ── Step 2: Machine ──
      const machineTitle = document.createElement('h3');
      machineTitle.className = 'cb-drawer__section-title';
      machineTitle.textContent = this._step2LabelText;
      body.appendChild(machineTitle);

      if (this.selectedMachine) {
        const card = this.refs.machineGrid.querySelector('[data-product-id="' + this.selectedMachine + '"]');
        if (card) {
          const items = document.createElement('div');
          items.className = 'cb-drawer__items';
          const item = this._createDrawerItem(card.dataset.productImage, card.dataset.productTitle);
          const changeBtn = document.createElement('button');
          changeBtn.className = 'cb-drawer__change-btn cb-pill-btn';
          changeBtn.textContent = 'Change';
          changeBtn.addEventListener('click', () => { this._closeDrawer(); this._goToStep(2); });
          item.querySelector('.cb-drawer__item-action').appendChild(changeBtn);
          items.appendChild(item);
          body.appendChild(items);
        }
      } else {
        const empty = document.createElement('p');
        empty.className = 'cb-drawer__empty';
        empty.textContent = 'No ' + this._step2LabelText.toLowerCase() + ' selected';
        body.appendChild(empty);
      }

      body.appendChild(this._createDivider());

      // ── Step 1: Capsules ──
      const capTitle = document.createElement('h3');
      capTitle.className = 'cb-drawer__section-title';
      capTitle.textContent = this._step1LabelText;
      body.appendChild(capTitle);

      if (this.capsuleItems.length > 0) {
        // Group by productId
        const grouped = {};
        this.capsuleItems.forEach(item => {
          if (!grouped[item.productId]) grouped[item.productId] = { ...item, qty: 0 };
          grouped[item.productId].qty++;
        });
        const capItems = document.createElement('div');
        capItems.className = 'cb-drawer__items';
        Object.values(grouped).forEach(g => {
          const item = this._createDrawerItem(g.image, g.title);
          const qty = this._createQtyControl(g.productId, g.qty, 'capsule');
          item.querySelector('.cb-drawer__item-action').appendChild(qty);
          capItems.appendChild(item);
        });
        body.appendChild(capItems);
      } else {
        const empty = document.createElement('p');
        empty.className = 'cb-drawer__empty';
        empty.textContent = 'No ' + this._step1LabelText.toLowerCase() + ' selected';
        body.appendChild(empty);
      }

      body.appendChild(this._createDivider());

      // ── Step 3: Accessories ──
      const accTitle = document.createElement('h3');
      accTitle.className = 'cb-drawer__section-title';
      accTitle.textContent = this._step3LabelText;
      body.appendChild(accTitle);

      if (this.accessorySet.size > 0) {
        const accItems = document.createElement('div');
        accItems.className = 'cb-drawer__items';
        this.accessorySet.forEach(pid => {
          const card = this.refs.accessoriesGrid.querySelector('[data-product-id="' + pid + '"]');
          if (!card) return;
          const item = this._createDrawerItem(card.dataset.productImage, card.dataset.productTitle);
          const removeBtn = document.createElement('button');
          removeBtn.className = 'cb-drawer__change-btn cb-pill-btn';
          removeBtn.textContent = 'Remove';
          removeBtn.addEventListener('click', () => { this._toggleAccessory(pid, false); this._renderDrawer(); });
          item.querySelector('.cb-drawer__item-action').appendChild(removeBtn);
          accItems.appendChild(item);
        });
        body.appendChild(accItems);
      } else {
        const empty = document.createElement('p');
        empty.className = 'cb-drawer__empty';
        empty.textContent = 'No ' + this._step3LabelText.toLowerCase() + ' selected';
        body.appendChild(empty);
      }

      // Totals
      this._updateDrawerTotals();

      // Checkout state
      // Accessories are optional — only capsules and a machine are required.
      const allSelected = this.totalCapsules > 0 && this.selectedMachine;
      this.refs.drawerCheckout.disabled = !allSelected;
    }

    _createDrawerItem(image, title) {
      const item = document.createElement('div');
      item.className = 'cb-drawer__item';
      item.innerHTML = '<div class="cb-drawer__item-image"><img src="' + (image || '') + '" alt="' + (title || '') + '" width="45" height="45"></div>' +
        '<div class="cb-drawer__item-info"><p class="cb-drawer__item-title">' + (title || '') + '</p></div>' +
        '<div class="cb-drawer__item-action"></div>';
      return item;
    }

    _createDivider() {
      const hr = document.createElement('hr');
      hr.className = 'cb-drawer__divider';
      return hr;
    }

    _createQtyControl(productId, qty, type) {
      const wrapper = document.createElement('div');
      wrapper.className = 'cb-drawer__qty';
      const minus = document.createElement('button');
      minus.className = 'cb-drawer__qty-btn';
      minus.innerHTML = '&ndash;';
      minus.setAttribute('aria-label', 'Decrease');
      const val = document.createElement('span');
      val.className = 'cb-drawer__qty-value';
      val.textContent = qty;
      const plus = document.createElement('button');
      plus.className = 'cb-drawer__qty-btn';
      plus.textContent = '+';
      plus.setAttribute('aria-label', 'Increase');

      if (type === 'capsule') {
        minus.addEventListener('click', () => this._drawerRemoveCapsule(productId));
        plus.addEventListener('click', () => { this._addItem(productId); this._renderDrawer(); });
      } else {
        minus.addEventListener('click', () => { this._toggleAccessory(productId, false); this._renderDrawer(); });
        plus.style.visibility = 'hidden';
      }

      wrapper.appendChild(minus);
      wrapper.appendChild(val);
      wrapper.appendChild(plus);
      return wrapper;
    }

    /* #8 — use price map instead of DOM queries */
    _updateDrawerTotals() {
      const idx = this.currentTierIndex;
      const pct = idx !== null ? this.tiers[idx].pct : 0;
      let originalTotal = 0;

      // Capsule prices
      this.capsuleItems.forEach(item => {
        originalTotal += this._priceMap[item.productId] || 0;
      });

      // Machine price
      if (this.selectedMachine) {
        originalTotal += this._priceMap[this.selectedMachine] || 0;
      }

      // Accessory prices
      this.accessorySet.forEach(pid => {
        originalTotal += this._priceMap[pid] || 0;
      });

      // Discount on total (matches Shopify's discount function rounding)
      const discountedTotal = originalTotal - Math.floor(originalTotal * pct / 100);

      if (pct > 0) {
        this.refs.drawerTotalOriginal.textContent = this._formatPrice(originalTotal);
        this.refs.drawerTotalOriginal.style.display = '';
      } else {
        this.refs.drawerTotalOriginal.style.display = 'none';
      }
      this.refs.drawerTotalSale.textContent = this._formatPrice(discountedTotal);
    }

    // ── Warning Modal (capsule removal from drawer) ──
    _drawerRemoveCapsule(productId) {
      // Check if removing would lower the tier
      const currentIdx = this.currentTierIndex;
      if (currentIdx !== null) {
        const qtyAfter = this.totalCapsules - 1;
        let newIdx = null;
        for (let i = this.tiers.length - 1; i >= 0; i--) {
          if (qtyAfter >= this.tiers[i].qty) { newIdx = i; break; }
        }
        if (newIdx === null || newIdx < currentIdx) {
          const fromPct = this.tiers[currentIdx].pct;
          const toPct = newIdx !== null ? this.tiers[newIdx].pct : 0;
          this._pendingRemove = { productId, type: 'capsule' };
          this.refs.warningText.innerHTML = 'Removing this cocktail will lower your savings from <strong>' + fromPct + '%</strong> to <strong>' + toPct + '%</strong>.';
          this.refs.warningDialog.showModal();
          return;
        }
      }
      this._removeItemByProduct(productId);
      this._renderDrawer();
    }

    _confirmRemove() {
      if (!this._pendingRemove) return;
      const { productId } = this._pendingRemove;
      this._pendingRemove = null;
      this.refs.warningDialog.close();
      this._removeItemByProduct(productId);
      this._renderDrawer();
    }

    _closeWarning() {
      this._pendingRemove = null;
      this.refs.warningDialog.close();
    }

    /* #12 — fetch response checking */
    async _checkout() {
      const btn = this.refs.drawerCheckout;
      if (btn.disabled) return;
      btn.disabled = true;
      btn.textContent = 'Processing…';

      const bundleId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      const idx = this.currentTierIndex;
      const pct = idx !== null ? this.tiers[idx].pct : 0;
      const items = [];

      // Capsules (grouped by variant) — role: bundleProduct
      const capsulesByVariant = {};
      this.capsuleItems.forEach(item => {
        if (!capsulesByVariant[item.variantId]) capsulesByVariant[item.variantId] = 0;
        capsulesByVariant[item.variantId]++;
      });
      Object.entries(capsulesByVariant).forEach(([variantId, qty]) => {
        items.push({ id: parseInt(variantId), quantity: qty, properties: { '_bundleId': bundleId, '_bundleRole': 'bundleProduct', '_bundlePct': String(pct) } });
      });

      // Machine — role: machine
      if (this.selectedMachine) {
        const card = this.refs.machineGrid.querySelector('[data-product-id="' + this.selectedMachine + '"]');
        if (card) items.push({ id: parseInt(card.dataset.variantId), quantity: 1, properties: { '_bundleId': bundleId, '_bundleRole': 'machine', '_bundlePct': String(pct) } });
      }

      // Accessories — role: accessory
      this.accessorySet.forEach(pid => {
        const card = this.refs.accessoriesGrid.querySelector('[data-product-id="' + pid + '"]');
        if (card) items.push({ id: parseInt(card.dataset.variantId), quantity: 1, properties: { '_bundleId': bundleId, '_bundleRole': 'accessory', '_bundlePct': String(pct) } });
      });

      try {
        const root = window.Shopify.routes.root || '/';
        const clearRes = await fetch(root + 'cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
        if (!clearRes.ok) throw new Error('Failed to clear cart');
        const addRes = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
        if (!addRes.ok) throw new Error('Failed to add items');
        sessionStorage.removeItem(this._storageKey());
        window.location.href = root + 'checkout';
      } catch (e) {
        btn.disabled = false;
        btn.textContent = 'Checkout';
      }
    }

    // ── Step Navigation ──
    /* #15 — store ResizeObserver reference for cleanup */
    _trackHeaderHeight() {
      const header = document.getElementById('shopify-section-header');
      if (!header) return;
      const update = () => {
        this.style.setProperty('--cb-header-height', header.offsetHeight + 'px');
      };
      update();
      this._headerRO = new ResizeObserver(update);
      this._headerRO.observe(header);
    }

    // A step is only "complete" when it still holds a valid selection — mirrors
    // the CTA advance conditions. Lets the completed check disappear if the user
    // goes back and empties an earlier step (e.g. removes all capsules).
    _isStepComplete(n) {
      if (n === 1) return this.totalCapsules > 0;
      if (n === 2) return !!this.selectedMachine;
      // Step 3 (accessories) is optional — reaching it is enough to count as complete.
      if (n === 3) return true;
      return false;
    }

    // Reflect active / completed / clickable state onto the step tabs. Called
    // both on navigation and on every render so the completed check stays in
    // sync when an earlier step is emptied (e.g. all capsules removed).
    _updateStepClasses() {
      this._steps.forEach(s => {
        const n = parseInt(s.dataset.step);
        s.classList.remove('active', 'completed');
        if (n === this.currentStep) s.classList.add('active');
        else if (n < this.currentStep && this._isStepComplete(n)) s.classList.add('completed');
        // Any step up to the furthest reached is navigable.
        s.classList.toggle('clickable', n <= this._maxStepReached);
      });
    }

    _goToStep(step) {
      this.currentStep = step;
      if (step > this._maxStepReached) this._maxStepReached = step;
      this._updateStepClasses();
      this._panels.forEach(p => {
        p.classList.toggle('active', parseInt(p.dataset.panel) === this.currentStep);
      });
      this._renderBottomBanner();
      this._saveState();
      const activeStep = this.querySelector('.cb-step.active');
      if (activeStep) activeStep.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  });
}
